import React from 'react';
import { format, isSameDay, isSameMonth, isToday } from 'date-fns';
import { Button } from '~/UIKit';
import classNames from 'classnames';
import {
  WHITE,
  MIDDLE_GRAY,
  CHARCOAL_GREY,
  PRIMARY_BLUE,
  FITOGRAM_BLUE_DARK_2,
  TRANSPARENT,
} from '~/Constants/Colors.constants';
import { makeStyles } from '~/UIKit/Style';
import { FONT_SIZE_SMALL } from '~/Constants/UI.constants';
import { DATE_PICKER_DAY_WIDTH } from '~/Screens/Calendar/CalendarDatePicker/constants';

const useStyles = makeStyles(
  (theme) => ({
    container: {
      height: 29,
      display: 'flex',
      maxWidth: DATE_PICKER_DAY_WIDTH,
      overflow: 'hidden',
      position: 'relative',
      flexBasis: DATE_PICKER_DAY_WIDTH,
      alignItems: 'center',
      justifyContent: 'center',
      [theme.breakpoints.down('md')]: {
        height: 40,
      },
    },
    hasEvents: {
      '&:after': {
        content: '""',
        left: '50%',
        right: '50%',
        width: 4,
        height: 4,
        bottom: 0,
        position: 'absolute',
        transform: 'translate(-50%, -50%)',
        borderRadius: '100%',
        backgroundColor: CHARCOAL_GREY,
      },
    },
    day: {
      color: CHARCOAL_GREY,
      width: 28,
      height: 28,
      display: 'flex',
      position: 'relative',
      minWidth: 0,
      fontSize: FONT_SIZE_SMALL,
      textAlign: 'center',
      lineHeight: 'initial',
      alignItems: 'center',
      borderRadius: '100%',
      letterSpacing: 0.5,
      justifyContent: 'center',
      border: `1px solid ${TRANSPARENT}`,
      '&:hover': {
        color: WHITE,
        backgroundColor: FITOGRAM_BLUE_DARK_2,
        '&:after': {
          backgroundColor: WHITE,
        },
      },
    },
    anotherMonth: {
      color: MIDDLE_GRAY,
      '&:after': {
        backgroundColor: MIDDLE_GRAY,
      },
    },
    selectedDay: {
      color: WHITE,
      backgroundColor: PRIMARY_BLUE,
      '&:after': {
        backgroundColor: WHITE,
      },
    },
    today: {
      border: `1px solid ${PRIMARY_BLUE}`,
    },
  }),
  { name: 'CalendarDatePickerDay' },
);

interface CalendarDayProps {
  day: Date;
  onClick: (day: Date) => void;
  focusedDay: Date;
  selectedDay: Date;
}

export const CalendarDatePickerDay = ({
  day,
  onClick,
  focusedDay,
  selectedDay,
}: CalendarDayProps) => {
  const { classes } = useStyles();

  return (
    <div className={classes.container} data-testid={`CalendarDay-${format(day, 'dd')}`}>
      <Button
        className={classNames(classes.day, {
          [classes.today]: isToday(day) && !isSameDay(selectedDay, day),
          [classes.anotherMonth]: !isSameMonth(focusedDay, day),
          [classes.selectedDay]: isSameDay(selectedDay, day),
        })}
        type="button"
        onClick={() => onClick(day)}
        data-testid={`CalendarDayValue-${format(day, 'dd')}`}
      >
        {format(day, 'd')}
      </Button>
    </div>
  );
};
